import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, UserCog, Pencil } from "lucide-react";

const PERMS = [
  { key: "can_manage_products", label: "Kelola Produk & Stok" },
  { key: "can_manage_customers", label: "Kelola Pelanggan" },
  { key: "can_view_reports", label: "Lihat Laporan" },
];

export default function Staff() {
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", permissions: {} });

  const load = () => api.get("/staff").then((r) => setStaff(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name: "", email: "", password: "", permissions: {} }); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ name: s.name, email: s.email, password: "", permissions: s.permissions || {} }); setOpen(true); };
  const togglePerm = (k) => setForm({ ...form, permissions: { ...form.permissions, [k]: !form.permissions[k] } });

  const save = async () => {
    try {
      if (editing) await api.put(`/staff/${editing.id}`, { name: form.name, password: form.password || undefined, permissions: form.permissions });
      else await api.post("/staff", { name: form.name, email: form.email, password: form.password, permissions: form.permissions });
      toast.success("Kasir disimpan"); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (s) => {
    try { await api.delete(`/staff/${s.id}`); toast.success("Kasir dihapus"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="staff-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Kasir & Staff</h1>
          <p className="text-muted-foreground text-sm">Tambahkan kasir dan atur izin akses mereka.</p>
        </div>
        <Button onClick={openNew} className="rounded-lg" data-testid="add-staff-btn"><Plus className="w-4 h-4 mr-1" /> Tambah Kasir</Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {staff.map((s) => (
          <Card key={s.id} className="p-5 border-slate-200 rounded-xl shadow-sm" data-testid={`staff-${s.id}`}>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-accent text-primary flex items-center justify-center"><UserCog className="w-5 h-5" /></div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{s.name}</div>
                <div className="text-xs text-muted-foreground truncate">{s.email}</div>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {PERMS.filter((p) => s.permissions?.[p.key]).map((p) => (
                <span key={p.key} className="text-[10px] font-medium bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">{p.label}</span>
              ))}
              {!PERMS.some((p) => s.permissions?.[p.key]) && <span className="text-xs text-muted-foreground">Hanya akses POS & transaksi</span>}
            </div>
            <div className="mt-4 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => openEdit(s)} className="rounded-lg flex-1"><Pencil className="w-3.5 h-3.5 mr-1" /> Edit</Button>
              <Button size="sm" variant="outline" onClick={() => remove(s)} className="rounded-lg text-red-600 hover:bg-red-50" data-testid={`delete-staff-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </Card>
        ))}
        {staff.length === 0 && <div className="col-span-full text-center py-12 text-muted-foreground">Belum ada kasir. Tambahkan kasir pertama Anda.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="staff-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Kasir" : "Tambah Kasir"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nama</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-lg" data-testid="staff-name-input" /></div>
            {!editing && <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-lg" data-testid="staff-email-input" /></div>}
            <div><Label>{editing ? "Kata Sandi Baru (opsional)" : "Kata Sandi"}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 rounded-lg" data-testid="staff-password-input" /></div>
            <div className="pt-1">
              <Label>Izin Akses</Label>
              <div className="mt-2 space-y-2">
                {PERMS.map((p) => (
                  <div key={p.key} className="flex items-center justify-between p-2.5 rounded-lg border border-slate-200">
                    <span className="text-sm">{p.label}</span>
                    <Switch checked={!!form.permissions[p.key]} onCheckedChange={() => togglePerm(p.key)} data-testid={`perm-${p.key}`} />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-lg">Batal</Button>
            <Button onClick={save} className="rounded-lg" data-testid="save-staff-btn">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
